import React, { useCallback, useState, useEffect, useRef } from "react";
import { Button, Alert } from "@mui/material";
import Select from "../../ui/PortalSelect";
import { Country, State, City } from "country-state-city";
import IATA from "./IATA.json";
import Swal from 'sweetalert2';
import { baseURL } from '../backend_url';
import EditIcon from '@mui/icons-material/Edit';
import DeleteForeverIcon from '@mui/icons-material/DeleteForever';
import { fetchJson, loadAccessibleCompanies, readSessionUser } from '../accountAccess';
import { buildCompanyOptions, findCompanyById } from './companySelection';



export default function UpdateForm() {

  const [companyDetail, setcompanyDetail] = useState([]);
  const [selectedCompanyId, setSelectedCompanyId] = useState("");
  const [company_id, setcompany_id] = useState("");
  const [locationDetail, setLocationDetail] = useState([{}]);
  const [numOfLoc, setNumOfLoc] = useState(0);
  const [successMsg, setSuccessMsg] = useState("");
  const [successMsg2, setSuccessMsg2] = useState("");
  const [loading, setLoading] = useState(false);
  const [addNewClicked, setAddNewClicked] = useState(false);
  const [locationName, setLocationName] = useState('');
  const [country, setCountry] = useState("");
  const [state, setState] = useState("");
  const [stateData, setStateData] = useState([]);
  const [cityData, setCityData] = useState([]);
  const [city, setCity] = useState("");
  const [airportCodeData, setAirtportCodeData] = useState([]);
  const [airportCode, setAirtportCode] = useState("");
  const [activeLocation, setActiveLocation] = useState(0);
  const [errorNumOfLoc, setErrorNumOfLoc] = useState("");
  const [maxActiveLocLimit, setMaxActiveLocLimit] = useState("");
  const [locationType, setLocationType] = useState("");
  const [locationTypeData, setLocationTypeData] = useState([]);
  const [validationAlert, setValidationAlert] = useState("");
  const [loggedInUserDetail, setLoggedInUserDetail] = useState({});

  const [updateClicked, setUpdateClicked] = useState(false);
  const [locationID, setLocationID] = useState("")
  const selectedCompanyIdRef = useRef('');
  const selectedCompany = findCompanyById(companyDetail, selectedCompanyId);
  const companyName = selectedCompany?.company_name || '';

  const loadCompanyLocations = useCallback(async (selectedCompanyId) => {
    const accountId = String(selectedCompanyId || '').trim();
    if (!accountId) {
      throw new Error('Please select an account before loading locations.');
    }

    const data = await fetchJson(
      baseURL + '/get_company_location_inactive_active/' + encodeURIComponent(accountId)
      + '?companyNumber=' + encodeURIComponent(accountId),
      {
        method: 'GET',
        mode: 'cors',
        headers: { 'Content-Type': 'application/json' },
      },
      'Location list'
    );

    if (!data || !Array.isArray(data.res)) {
      throw new Error('Location list returned an unexpected response.');
    }

    // Ignore a late response from an account the user has already left.
    // This prevents locations from one account being rendered under another.
    if (selectedCompanyIdRef.current === accountId) {
      setLocationDetail(data.res);
      setActiveLocation(Number(data.active_locs) || 0);
      setNumOfLoc(Number(data.max_num_of_locs) || 0);
    }
    return data.res;
  }, []);


  //This is for scrolling
  const titleRef = useRef()

  function handleBackClick() {
    window.requestAnimationFrame(() => {
      titleRef.current?.scrollIntoView({ behavior: 'smooth' });
    });
  }

  //API call
  useEffect(() => {
    const { user, error } = readSessionUser();

    if (error) {
      setLoggedInUserDetail({});
      setcompanyDetail([]);
      setValidationAlert(error);
      return;
    }

    setLoggedInUserDetail(user);
    loadAccessibleCompanies(baseURL, user, 'AccountLocations_Account Access')
      .then(setcompanyDetail)
      .catch((loadError) => {
        setcompanyDetail([]);
        setValidationAlert(loadError.message || 'Accounts could not be loaded.');
      });
  }, []);

  //Triggered on Company Name Dropdown Changes
  useEffect(() => {
    setSuccessMsg("");
    setMaxActiveLocLimit("");
    setValidationAlert("");

    if (!selectedCompanyId) {
      selectedCompanyIdRef.current = '';
      setcompany_id('');
      setLocationDetail([]);
      return;
    }

    const companyForLoad = findCompanyById(companyDetail, selectedCompanyId);
    if (!companyForLoad) {
      selectedCompanyIdRef.current = '';
      setcompany_id('');
      setLocationDetail([]);
      setValidationAlert('The selected account is no longer available. Please select it again.');
      return;
    }

    const accountId = String(companyForLoad._id);
    selectedCompanyIdRef.current = accountId;

    // The selected account must be available immediately to every write. The
    // previous implementation waited for an unrelated user-details request,
    // so a slow/failed request could submit with a blank or stale account ID.
    setcompany_id(accountId);
    setNumOfLoc(Number(companyForLoad.no_of_loc) || 0);
    setLocationDetail([]);

    loadCompanyLocations(accountId).catch((loadError) => {
      setLocationDetail([]);
      setValidationAlert(loadError.message || 'Locations could not be loaded.');
    });

    setLocationTypeData([
      { "name": "On Terminal" },
      { "name": "Off Terminal" },
      { "name": "City Location/Downtown" },
      { "name": "Hotel Location" },
      { "name": "Railway Station" },
      { "name": "Bus/Tram" },
    ]);
  }, [companyDetail, selectedCompanyId, loadCompanyLocations]);


  //useEffect for checking location count starts
  useEffect(() => {

    if (activeLocation >= numOfLoc) {
      setAddNewClicked(false);
      setErrorNumOfLoc("You've exceeded the maximum allowed locations you can add. Please contact support@caledontechnologies.com to increase your limit.");
    }
    else {
      setErrorNumOfLoc("");
    }


  }, [activeLocation, numOfLoc]);
  //useEffect for checking location count ends

  const handleLocationStatusChange = async (location_id, status, location_name) => {
    const result = await Swal.fire({
      text: 'You are changing location status of ' + location_name + ' to ' + status + '. Are you sure?',
      showCancelButton: true,
      confirmButtonText: 'Yes',
    });

    if (!result.isConfirmed) {
      return;
    }

    let statusWasChanged = false;
    try {
      const data = await fetchJson(
        baseURL + "/change_company_location_status",
        {
          method: "PUT",
          mode: 'cors',
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            is_active: status,
            location_id: location_id,
            company_id: company_id.toString(),
            companyNumber: company_id.toString(),
            updated_by: loggedInUserDetail.userEmail,
          }),
        },
        'Location status update'
      );

      if (data === false) {
        setMaxActiveLocLimit("Maximum active location you can have is " + numOfLoc + " and You have already touched the limit. Please contact support@caledontechnologies.com to increase your limit.");
        return;
      }
      if (!Array.isArray(data)) {
        throw new Error('Location status update returned an unexpected response.');
      }
      statusWasChanged = true;

      const refreshedLocations = await loadCompanyLocations(company_id);
      const changedLocation = refreshedLocations.find(
        (item) => item.location_id === location_id
      );
      const expectedActive = String(status).toLowerCase() === 'active';
      if (!changedLocation || changedLocation.is_active !== expectedActive) {
        throw new Error('The updated status was not returned by the location list.');
      }
      setMaxActiveLocLimit("");
      await Swal.fire('Status Changed to ' + status, '', 'success');
    } catch (error) {
      const message = statusWasChanged
        ? 'The status was changed, but the location list could not be refreshed. Please reload the page.'
        : (error.message || 'Location status could not be updated.');
      setValidationAlert(message);
      await Swal.fire(
        statusWasChanged ? 'Location list was not refreshed' : 'Location status was not changed',
        message,
        'error'
      );
    }
  }

  //Option for company name
  const companyNameOptions = buildCompanyOptions(companyDetail);

  const handleInputChangeCompanyName = characterEntered => {
    setSelectedCompanyId(characterEntered?.value || '');
  };


  const handleCountry = (value) => {
    console.log("-----country-----")
    console.log(value)
    const data = State.getAllStates();
    let filteredData = [];
    for (let item of data) {
      if (item?.countryCode === value?.isoCode) filteredData.push(item);
    }
    setStateData(filteredData);
    setState("");

  };

  const handleState = (value) => {
    console.log("-----state-----")
    console.log(value)
    let filteredData = [];
    const data = City.getAllCities();
    for (let item of data) {
      if (
        value?.countryCode === item?.countryCode &&
        value?.isoCode === item?.stateCode
      ) {
        filteredData.push(item);
      }
    }
    setCityData(filteredData);
    setCity("");
  };

  const handleCity = (value) => {
    console.log("-----city-----")
    console.log(value)
    let filteredData = [];
    for (let item of IATA) {
      if (
        value.name != '' && value.name != null &&
        value.name.toString().trim().toLowerCase() === item?.city.toString().trim().toLowerCase() &&

        item.iata != ''
      ) {
        filteredData.push(item);
      }
    }

    if (filteredData.length === 0) {
      for (let item of IATA) {
        if (
          country.name != '' && country.name != null &&
          country.name.toString().trim().toLowerCase() === item?.country.toString().trim().toLowerCase() &&
          item.iata != ''
        ) {
          filteredData.push(item);
        }
      }

    }

    setAirtportCodeData(filteredData);

  }

  const submitHandler = async (e) => {

    e.preventDefault();

    if (!company_id) {
      setValidationAlert("Please select an account before adding a location");
      return;
    }
    else if (activeLocation >= numOfLoc) {
      // setAddNewClicked(false);
      setErrorNumOfLoc("You've exceeded the maximum allowed locations you can add. Please contact support@caledontechnologies.com to increase your limit.");
    }
    else {


      if (locationName.toString().trim() === "") {
        setValidationAlert("Please enter Location Name");
        return;
      }

      else if (!country || !country.name) {
        setValidationAlert("Please select Country");
        return;
      }

      else if (!state || !state.name) {
        setValidationAlert("Please select State");
        return;
      }

      else if (!city || !city.name) {
        setValidationAlert("Please select City");
        return;
      }

      else if (!airportCode || !airportCode.iata) {
        setValidationAlert("GDS Code can't be empty");
        return;
      }
      else if (!locationType || !locationType.name) {
        setValidationAlert("Please select Location Type");
        return;
      }



      else {
        setLoading(true);
        let locationWasSaved = false;
        const submittedLocationName = locationName.toString().trim();
        try {
          const data = await fetchJson(
            baseURL + "/save_company_location",
            {
              method: "POST",
              mode: 'cors',
              headers: { "Content-Type": "application/json" },
              body: JSON.stringify({
                'company_id': company_id.toString(),
                'companyNumber': company_id.toString(),
                'company_name': companyName,
                'location_name': submittedLocationName,
                'country': country.name,
                'state': state.name,
                'city': city.name,
                'city_code': (airportCode != null && airportCode !== '' && airportCode.iata !== undefined) ? airportCode.iata : "",
                'location_type': locationType.name,
                'created_by': loggedInUserDetail.userEmail,
                'updated_by': loggedInUserDetail.userEmail,
              }),
            },
            'Add location'
          );

          if (data === 'Already Present') {
            await Swal.fire({
              position: 'center',
              icon: 'error',
              title: 'Location Name Already Exists',
              showConfirmButton: false,
              timer: 3000
            });
            return;
          }
          if (data !== 'success') {
            throw new Error(typeof data === 'string' ? data : 'Add location returned an unexpected response.');
          }
          locationWasSaved = true;

          // Do not report success until the persisted record has been read
          // back into the visible Existing Locations table.
          const refreshedLocations = await loadCompanyLocations(company_id);
          const savedLocationIsVisible = refreshedLocations.some(
            (item) => String(item.location_name || '').trim().toLocaleLowerCase()
              === submittedLocationName.toLocaleLowerCase()
          );
          if (!savedLocationIsVisible) {
            throw new Error('The saved location was not returned by the location list.');
          }

          setCountry(null);
          setState(null);
          setCity(null);
          setAirtportCode("");
          setLocationName("");
          setLocationType("");
          setValidationAlert("");
          await Swal.fire({
            position: 'center',
            icon: 'success',
            title: 'A new location has been added successfully',
            showConfirmButton: false,
            timer: 3000
          });
        } catch (error) {
          const message = locationWasSaved
            ? 'The location was saved, but the location list could not be refreshed. Please reload the page.'
            : (error.message || 'The location could not be added. Please try again.');
          setValidationAlert(message);
          await Swal.fire({
            position: 'center',
            icon: 'error',
            title: locationWasSaved ? 'Location list was not refreshed' : 'Location was not added',
            text: message,
          });
        } finally {
          setLoading(false);
        }
      }

    }

  }

 

  const handleUpdate = (val) => {

    const data1 = Country.getAllCountries();
    let filteredData1 = [];
    for (let item of data1) {
      if (val.country === item?.name) {
        filteredData1.push(item);
        break;
      }
    }

    const data = State.getAllStates();
    let filteredData = [];
    for (let item of data) {
      if (item?.countryCode === filteredData1[0]?.isoCode) filteredData.push(item);
    }
    setStateData(filteredData);


    const data2 = State.getAllStates();
    let filteredData2 = [];
    for (let item of data2) {
      if (val.state === item?.name) {
        filteredData2.push(item);
        break;
      }
    }


    const data3 = City.getAllCities();
    let filteredData3 = [];
    for (let item of data3) {

      if (filteredData1[0]?.isoCode === item?.countryCode &&
        filteredData2[0]?.isoCode === item?.stateCode) filteredData3.push(item);
    }


    setCityData(filteredData3);


    setAddNewClicked(false);
    setUpdateClicked(true);

    setAirtportCode(val.city_code)
    setCity(val.city)
    setState(val.state)
    setCountry(val.country)
    setLocationName(val.location_name)
    setLocationType(val.location_type)
    setLocationID(val.location_id)


  }


  const updateData1 = async (e) => {

    e.preventDefault();


    if (locationName.toString().trim() == "") {
      Swal.fire({
        position: 'center',
        icon: 'error',
        title: 'Please enter Location Name',
        showConfirmButton: false,
        timer: 3000
      })
      return;
    }

    if (locationType === null || locationType === "") {
      Swal.fire({
        position: 'center',
        icon: 'error',
        title: 'Please select Location Type',
        showConfirmButton: false,
        timer: 3000
      })
      return;
    }
    else {
      setLoading(true);
      let locationWasUpdated = false;
      const submittedLocationName = locationName.toString().trim();
      try {
        const data = await fetchJson(
          baseURL + "/update_company_location",
          {
            method: "POST",
            mode: 'cors',
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({
              'location_id': locationID,
              'company_id': company_id.toString(),
              'companyNumber': company_id.toString(),
              'company_name': companyName,
              'location_name': submittedLocationName,
              'state': state?.name === undefined ? state : state.name,
              'city': city?.name === undefined ? city : city.name,
              'location_type': locationType,
              'updated_by': loggedInUserDetail.userEmail,
            }),
          },
          'Update location'
        );

        if (data === 'Already Present') {
          await Swal.fire({
              position: 'center',
              icon: 'error',
              title: 'Location Name Already Exists',
              showConfirmButton: false,
              timer: 3000
          });
          return;
        }
        if (data !== 'success') {
          throw new Error(typeof data === 'string' ? data : 'Update location returned an unexpected response.');
        }
        locationWasUpdated = true;
        const refreshedLocations = await loadCompanyLocations(company_id);
        const updatedLocationIsVisible = refreshedLocations.some(
          (item) => item.location_id === locationID
            && String(item.location_name || '').trim().toLocaleLowerCase()
              === submittedLocationName.toLocaleLowerCase()
        );
        if (!updatedLocationIsVisible) {
          throw new Error('The updated location was not returned by the location list.');
        }

        setUpdateClicked(false);
        setCountry(null);
        setState(null);
        setCity(null);
        setAirtportCode("");
        setLocationName("");
        setLocationType("");
        setValidationAlert("");
        await Swal.fire({
          position: 'center',
          icon: 'success',
          title: 'Location has been updated successfully',
          showConfirmButton: false,
          timer: 3000
        });
      } catch (error) {
        const message = locationWasUpdated
          ? 'The location was updated, but the location list could not be refreshed. Please reload the page.'
          : (error.message || 'The location could not be updated. Please try again.');
        setValidationAlert(message);
        await Swal.fire('Location update failed', message, 'error');
      } finally {
        setLoading(false);
      }
    }
  }


  const handleDelete = async (id) => {
    const result = await Swal.fire({
      title: 'Do you want to delete this location ?',
      showCancelButton: true,
      confirmButtonText: 'Yes',
    });
    if (!result.isConfirmed) {
      return;
    }

    let locationWasDeleted = false;
    try {
      const data = await fetchJson(
        baseURL + "/delete_location/" + encodeURIComponent(id)
        + '?companyNumber=' + encodeURIComponent(company_id.toString()),
        { method: 'DELETE' },
        'Delete location'
      );
      if (data !== 'success') {
        throw new Error(typeof data === 'string' ? data : 'Delete location returned an unexpected response.');
      }
      locationWasDeleted = true;

      const refreshedLocations = await loadCompanyLocations(company_id);
      if (refreshedLocations.some((item) => item.location_id === id)) {
        throw new Error('The deleted location is still present in the location list.');
      }
      await Swal.fire({
        position: 'center',
        icon: 'success',
        title: "Deleted Successfully",
        showConfirmButton: false,
        timer: 3000
      });
    } catch (error) {
      const message = locationWasDeleted
        ? 'The location was deleted, but the location list could not be refreshed. Please reload the page.'
        : (error.message || 'The location could not be deleted. Please try again.');
      setValidationAlert(message);
      await Swal.fire(
        locationWasDeleted ? 'Location list was not refreshed' : 'Location was not deleted',
        message,
        'error'
      );
    }
  }



  return (
    <>
      <div className="row pt-2">
        <div className="col-8 mx-3">
          <h5>Select Account to edit details:</h5>
        </div>
      </div>
      <div className="row pt-4">
        {/* <div className="col-1"></div> */}
        <div className="col-9 mx-3">
          <form className="row g-3">

            <div className="col-md-2 mb-3">
              <label htmlFor="company_name" className="form-label" style={{ marginTop: 5 }}>
                Account Name
              </label>
            </div>

            <div className="col-md-5 mb-3">

              <Select

                value={companyNameOptions.find(
                  (option) => option.value === selectedCompanyId
                ) || null}
                inputId="company_name"
                name="company_name"
                options={companyNameOptions}
                onChange={handleInputChangeCompanyName}
                placeholder="Select Account name"

              />

            </div>


            <div className="col-md-1 mb-3"></div>

            <div className="col-md-2 mb-3">
              {/* <label htmlFor="company_id" className="form-label" style={{ marginTop: 5 }}>
                Account ID
              </label> */}
              <label htmlFor="company_name" className="form-label">
                Maximum number of location allowed
              </label>
            </div>
            <div className="col-md-2 mb-3">
              {/* <input
                type="text"
                className="form-control"
                value={company_id}
                id="company_id"
                disabled
              /> */}
              <input
                type="text"
                className="form-control"
                value={numOfLoc}
                id="no_of_loc"
                disabled
              />
            </div>

            <div className="col-md-9"></div>
          </form>
        </div>

      </div>


      <div className="row pt-1">

        <div className="col-md-10 mx-3">
          <Button variant="contained" onClick={() => { setAddNewClicked(!addNewClicked); setUpdateClicked(false); handleBackClick(); setLocationName(""); }}>Add New Location</Button>

        </div>
      </div>

      {/* Add New Location starts */}
      {addNewClicked ? (

        companyName === "" ? (
          <div className="row pt-2">
            <div className="col-10">
              <Alert severity="error">
                Please select an account to proceed ahead
              </Alert> </div></div>) :
          (
            errorNumOfLoc !== "" ? (

              <div className="row pt-1">
                <div className="col-10 mx-3">
                  <Alert severity="error">
                    {errorNumOfLoc}
                  </Alert> </div></div>
            ) :
              (
                <div className="row pt-2" >

                  <div className="col-lg-10 mx-3">
                    {
                      validationAlert ?
                        <Alert severity="error">
                          {validationAlert}
                        </Alert> : ""
                    }

                    <form onSubmit={submitHandler} ref={titleRef}>
                      <h5 className=" mb-1 mt-2">Add New Location </h5>


                      <table className="table" >

                        <thead>
                          <tr>

                            <th scope="col">Location Name*</th>
                            <th scope="col">Country*</th>
                            <th scope="col">State*</th>
                            <th scope="col">City/Town*</th>
                            <th scope="col">GDS Code</th>
                            <th scope="col">Location Type*</th>

                          </tr>
                        </thead>
                        <tbody>

                          <tr>
                            <td>
                              <input
                                type="text"
                                className="form-control"
                                value={locationName}
                                onChange={(e) => setLocationName(e.target.value)}
                                id="Location_name"

                              />
                            </td>
                            <td>
                              <Select

                                placeholder={"Select Country"}
                                options={Country.getAllCountries()}
                                value={country}
                                getOptionLabel={(options) => options.name}
                                getOptionValue={(options) => options.name}
                                onChange={(e) => {
                                  setCountry(e);
                                  handleCountry(e);
                                }}
                              />
                            </td>
                            <td>
                              <Select

                                placeholder={"Select state"}
                                value={state}
                                options={stateData}
                                getOptionLabel={(options) => options.name}
                                getOptionValue={(options) => options.name}
                                onChange={(e) => {
                                  setState(e);
                                  handleState(e);
                                }}
                              />
                            </td>
                            <td>
                              <Select

                                placeholder={"Select city"}
                                value={city}
                                options={cityData}
                                getOptionLabel={(options) => options.name}
                                getOptionValue={(options) => options.name}
                                onChange={(e) => {
                                  setCity(e);
                                  handleCity(e);
                                }}
                              />
                            </td>

                            <td>

                              <Select

                                placeholder={"Select city code"}
                                value={airportCode}
                                options={airportCodeData}
                                getOptionLabel={(options) => options.name + " - " + options.iata}
                                getOptionValue={(options) => options.name + " - " + options.iata}
                                onChange={(e) => {
                                  setAirtportCode(e);
                                }}
                              />
                            </td>

                            <td>
                              <Select

                                placeholder={"Select Location Type"}
                                value={locationType}
                                options={locationTypeData}
                                getOptionLabel={(options) => options.name}
                                getOptionValue={(options) => options.name}
                                onChange={(e) => {
                                  setLocationType(e);

                                }}
                              />

                            </td>

                          </tr>

                        </tbody>
                      </table>

                      <div className="row pt-2">
                        <div className="col-10 d-flex-wrap"></div>

                        <div className="col-2 d-flex justify-content-end">
                          {loading ? (
                            <button className="btn btn-success" type="button" disabled>
                              <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true" style={{ marginRight: 5 }}></span>
                              Submit
                            </button>
                          ) : (
                            <button className="btn btn-success" >
                              Submit
                            </button>
                          )}
                        </div>
                      </div>

                    </form>



                  </div>
                </div>
              )
          )) :
        updateClicked ?
          <>
            <div className="row pt-2" >

              <div className="col-lg-10 mx-3">
                <form ref={titleRef}>
                  <h5 className=" mb-1 mt-2">Edit Location </h5>


                  <table className="table" >

                    <thead>
                      <tr>

                        <th scope="col">Location Name*</th>
                        <th scope="col">Country*</th>
                        <th scope="col">State*</th>
                        <th scope="col">City/Town*</th>
                        <th scope="col">GDS Code</th>
                        <th scope="col">Location Type*</th>

                      </tr>
                    </thead>
                    <tbody>

                      <tr>
                        <td>
                          <input
                            type="text"
                            className="form-control"
                            value={locationName}
                            onChange={(e) => setLocationName(e.target.value)}
                            id="Location_name"

                          />
                        </td>
                        <td>
                          <Select

                            placeholder={"Select Country"}
                            options={Country.getAllCountries()}
                            value={country.name === undefined ? { name: country, value: country } : country}
                            getOptionLabel={(options) => options.name}
                            getOptionValue={(options) => options.name}
                            onChange={(e) => {
                              setCountry(e);
                              handleCountry(e);
                            }}
                            isDisabled={true}
                          />
                        </td>
                        <td>
                          <Select

                            placeholder={"Select state"}
                            value={state.name === undefined ? { name: state, value: state } : state}
                            options={stateData}
                            getOptionLabel={(options) => options.name}
                            getOptionValue={(options) => options.name}
                            onChange={(e) => {
                              setState(e);
                              handleState(e);
                            }}
                          />
                        </td>
                        <td>
                          <Select

                            placeholder={"Select city"}
                            value={city.name === undefined ? { name: city, value: city } : city}
                            options={cityData}
                            getOptionLabel={(options) => options.name}
                            getOptionValue={(options) => options.name}
                            onChange={(e) => {
                              setCity(e);
                              handleCity(e);
                            }}
                          />
                        </td>

                        <td>

                          {airportCode}
                        </td>

                        <td>
                          {locationType.indexOf('Terminal') > 0 ?
                            <select class="form-select" value={locationType} onChange={(e) => setLocationType(e.target.value)}>
                              <option value="On Terminal">On Terminal</option>
                              <option value="Off Terminal">Off Terminal</option>
                            </select>
                            :
                            locationType
                          }
                        </td>

                      </tr>

                    </tbody>
                  </table>

                  <div className="row pt-2">
                    <div className="col-10 d-flex-wrap"></div>

                    <div className="col-2 d-flex justify-content-end">
                      {loading ? (
                        <button className="btn btn-success" type="button" disabled>
                          <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true" style={{ marginRight: 5 }}></span>
                          Update
                        </button>
                      ) : (
                        <button className="btn btn-success" onClick={(e) => { updateData1(e); }} >
                          Update
                        </button>
                      )}
                    </div>
                  </div>

                </form>
              </div>
            </div>
          </> : ""
      }
      {/* Add New Location ends */}

      {/* existinng Location starts */}
      <div className="row pt-3">
        <div className="col-md-11 mx-3 ">
          <h5 className="mb-3">Existing Location(s)</h5>
          {successMsg ?
            (
              <div className="alert alert-success alert-dismissible fade show" role="alert">
                {successMsg}
                <button type="button" className="btn-close" data-bs-dismiss="alert" aria-label="Close" onClick={() => setSuccessMsg('')}></button>
              </div>
            )
            : ("")}
          {maxActiveLocLimit ? (
            <Alert severity="error">
              {maxActiveLocLimit}
            </Alert>) : ""
          }
          <div
            className="mvp-table-scroll mvp-table-scroll--wide"
            role="region"
            aria-label="Existing locations"
            tabIndex="0"
          >
          <table className="table pt-2">
            <thead>
              <tr>
                <th scope="col">#</th>
                <th scope="col">Location  Name</th>
                <th scope="col">Location ID</th>
                <th scope="col">Country</th>
                <th scope="col">State</th>
                <th scope="col">City/Town*</th>
                <th scope="col">Location Type</th>
                <th scope="col">Location Status</th>
                <th scope="col">Edit</th>
                <th scope="col">Delete</th>
              </tr>
            </thead>
            <tbody>

              {locationDetail.filter((value) => value?.location_id).length > 0
                ? locationDetail.filter((value) => value?.location_id).map((value, key) => (

                <tr key={value.location_id}>
                  <th scope="row" >{key + 1}</th>
                  <td >{value.location_name}</td>
                  <td >{value.location_id}</td>
                  <td >{value.country}</td>
                  <td>{value.state}</td>
                  <td >{value.city}</td>
                  <td >{value.location_type}</td>
                  <td>
                    <select className="form-select form-select-sm" value={value.is_active ? "active" : "inactive"} onChange={(e) => handleLocationStatusChange(value.location_id, e.target.value, value.location_name)}>
                      <option value="active">Active</option>
                      <option value="inactive">Inactive</option>
                    </select>
                  </td>
                  <td><button type="button" className="mvp-table-icon-action" aria-label={`Edit ${value.location_name || 'location'}`} onClick={() => handleUpdate(value)}><EditIcon /></button></td>
                  <td><button type="button" className="mvp-table-icon-action mvp-table-icon-action--danger" aria-label={`Delete ${value.location_name || 'location'}`} onClick={() => handleDelete(value.location_id)}><DeleteForeverIcon /></button></td>

                </tr>

              )) : (
                <tr>
                  <td colSpan="10" className="text-center text-muted py-4">
                    {company_id ? 'No locations found for this account.' : 'Select an account to view its locations.'}
                  </td>
                </tr>
              )}



            </tbody>
          </table>
          </div>
        </div>
      </div>
      {/* existinng Location ends */}

      <br /><br />
    </>
  );
}
